import type { Job } from '@/lib/schemas/db'

import { withUniversalTransitions } from './statusRules'

export type JobStatus = Job['status']

/** Canonical order of job statuses (drives transition ordering). */
export const JOB_STATUS_ORDER: JobStatus[] = [
  'NewJob',
  'ReadyToWork',
  'InProgress',
  'Blocked',
  'WaitingOnParts',
  'ReadyForCollection',
  'ReadyForCollectionUnsuccessful',
  'ReadyToInvoice',
  'Invoiced',
  'Cancelled',
]

/**
 * Ready-for-collection splits by repair outcome: the successful and
 * unsuccessful variants are the same workflow phase (see isReadyForCollection)
 * but carry different meaning and drive different customer messages. The
 * unsuccessful move requires a reason, the same way Blocked does.
 */
export const readyForCollectionStatuses = [
  'ReadyForCollection',
  'ReadyForCollectionUnsuccessful',
] as const

/** Moving into this status requires a reason (why the repair was unsuccessful). */
export const jobStatusRequiresReason = (status: JobStatus): boolean =>
  status === 'ReadyForCollectionUnsuccessful'

/** True for either ready-for-collection variant — use for phase checks. */
export function isReadyForCollection(status: JobStatus): boolean {
  return (readyForCollectionStatuses as readonly JobStatus[]).includes(status)
}

/**
 * Where a stalled job can go once it is unstalled. Shared by every blocking
 * status so a job waiting on parts moves exactly like a plainly blocked one —
 * the specific status narrows *why* it stopped, never what happens next.
 * The self-move is what lets the reason be updated (see withUniversalTransitions).
 */
const fromBlocking: JobStatus[] = [
  'InProgress',
  'ReadyForCollection',
  'ReadyForCollectionUnsuccessful',
  'Cancelled',
]

// A job can be cancelled from any working state — the customer withdraws, the
// device turns out not to be worth fixing — and cancelling always demands a
// reason (see statusRequiresReason). It is not reachable from a terminal state:
// an invoiced job is reopened first, so the cancellation is recorded against an
// open record rather than silently rewriting a closed one.
//
// The blocking statuses are reachable from everywhere via
// withUniversalTransitions, so they are not listed on each entry below.
const statusTransitions: Partial<Record<JobStatus, JobStatus[]>> = {
  NewJob: ['ReadyToWork', 'Cancelled'],
  ReadyToWork: [
    'InProgress',
    'ReadyForCollection',
    'ReadyForCollectionUnsuccessful',
    'Cancelled',
  ],
  InProgress: ['ReadyForCollection', 'ReadyForCollectionUnsuccessful', 'Cancelled'],
  Blocked: fromBlocking,
  WaitingOnParts: fromBlocking,
  ReadyForCollection: ['ReadyToInvoice', 'Cancelled'],
  ReadyForCollectionUnsuccessful: ['ReadyToInvoice', 'Cancelled'],
  ReadyToInvoice: [
    'Invoiced',
    'ReadyForCollection',
    'ReadyForCollectionUnsuccessful',
    'Cancelled',
  ],
  Invoiced: ['ReadyToWork'],
  Cancelled: ['ReadyToWork'],
}

const statusLabels: Record<JobStatus, string> = {
  NewJob: 'New Job',
  ReadyToWork: 'Ready to Work',
  InProgress: 'In Progress',
  Blocked: 'Blocked',
  WaitingOnParts: 'Waiting on Parts',
  ReadyForCollection: 'Ready for Collection (Success)',
  ReadyForCollectionUnsuccessful: 'Ready for Collection (Unsuccessful)',
  ReadyToInvoice: 'Ready to Invoice',
  Invoiced: 'Invoiced',
  Cancelled: 'Cancelled',
}

export function formatJobStatus(status: JobStatus) {
  return statusLabels[status] ?? status
}

export function getAllowedJobStatusTransitions(currentStatus: JobStatus): JobStatus[] {
  return withUniversalTransitions(currentStatus, statusTransitions[currentStatus] ?? [], {
    order: JOB_STATUS_ORDER,
    initial: 'NewJob',
  })
}
